/**
 * Custom Edge para exibir estatísticas de Teste A/B
 * Renderiza badge com porcentagem e contagem de execuções por variante
 */

import { memo } from 'react';
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
} from '@xyflow/react';

/**
 * Dados passados para o edge com estatísticas
 */
interface ABTestEdgeData {
  variant?: string;
  count?: number;
  percentage?: number;
  total?: number;
}

/**
 * Edge customizado que exibe analytics de Teste A/B
 * Mostra badge no centro da edge com variante, % e contagem
 */
export const ABTestEdge = memo(({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}: EdgeProps) => {
  const edgeData = data as ABTestEdgeData | undefined;
  
  // Calcula path da edge
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Se não tem dados de stats, renderiza edge normal com cor rosa
  if (!edgeData?.variant || edgeData.total === 0) {
    return (
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ ...style, strokeWidth: 2, stroke: '#ec4899' }}
      />
    );
  }

  const { variant, count = 0, percentage = 0, total = 0 } = edgeData;

  return (
    <>
      {/* Edge base com cor rosa do tema A/B Test */}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ 
          ...style, 
          strokeWidth: 2, 
          stroke: '#ec4899',
        }}
      />
      
      {/* Badge com estatísticas */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <div className="bg-[#ec4899]/20 border border-[#ec4899] rounded-md px-2 py-1 text-xs font-medium shadow-sm backdrop-blur-sm">
            <div className="flex flex-col items-center gap-0.5">
              {/* Variante e porcentagem */}
              <span className="text-[#ec4899] font-bold">
                {variant}: {percentage}%
              </span>
              {/* Contagem absoluta */}
              <span className="text-muted-foreground text-[10px]">
                ({count} de {total})
              </span>
            </div>
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

ABTestEdge.displayName = 'ABTestEdge';
